import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Share2, Loader2, Award, CheckCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';

const CANVAS_W = 800;
const CANVAS_H = 566;

const resolveVariable = (variable, cert) => {
  if (!variable) return '';
  if (variable === '{{name}}') return cert.user_name || '';
  if (variable === '{{course}}') return cert.course_title || '';
  if (variable === '{{date}}') return new Date(cert.completed_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' });
  if (variable === '{{cert_number}}') return cert.certificate_number || '';
  if (variable === '{{instructor}}') return cert.instructor || '';
  return '';
};

function CertificateElement({ el, cert, scale }) {
  const text = el.variable ? resolveVariable(el.variable, cert) : el.content;

  if (el.type === 'divider') {
    return (
      <div style={{
        position: 'absolute', left: el.x * scale, top: el.y * scale,
        width: el.w * scale, height: el.h * scale,
        backgroundColor: el.color, opacity: el.opacity ?? 1, borderRadius: 2,
      }} />
    );
  }
  if (el.type === 'icon' && el.content === 'award') {
    return (
      <div style={{ position: 'absolute', left: el.x * scale, top: el.y * scale, width: el.w * scale, height: el.h * scale }}>
        <Award style={{ width: '100%', height: '100%', color: el.color, opacity: el.opacity ?? 1 }} />
      </div>
    );
  }
  if (el.type === 'image') {
    return (
      <img src={el.content} alt="" style={{
        position: 'absolute', left: el.x * scale, top: el.y * scale,
        width: el.w * scale, height: el.h * scale, objectFit: 'contain',
        opacity: el.opacity ?? 1,
      }} />
    );
  }
  // text
  return (
    <div style={{
      position: 'absolute', left: el.x * scale, top: el.y * scale,
      width: el.w * scale, height: el.h * scale,
      display: 'flex', alignItems: 'center',
      justifyContent: el.textAlign === 'left' ? 'flex-start' : el.textAlign === 'right' ? 'flex-end' : 'center',
      fontSize: (el.fontSize || 16) * scale, fontFamily: el.fontFamily || 'sans-serif',
      fontWeight: el.fontWeight || 'normal', fontStyle: el.fontStyle || 'normal',
      color: el.color, textAlign: el.textAlign || 'center', opacity: el.opacity ?? 1,
      letterSpacing: (el.letterSpacing || 0) * scale, textTransform: el.textTransform || 'none',
      overflow: 'hidden', whiteSpace: 'nowrap', lineHeight: 1.2,
    }}>
      {text}
    </div>
  );
}

export default function CustomerCertificate() {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [design, setDesign] = useState({});
  const [loading, setLoading] = useState(true);
  const certRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const res = await api.get(`/customer/certificates/${certificateId}`);
        setCertificate(res.data.certificate);
        setDesign(res.data.design || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificate();
  }, [certificateId]);

  useEffect(() => {
    const calcScale = () => {
      if (containerRef.current) {
        const containerW = containerRef.current.offsetWidth;
        setScale(Math.min(containerW / CANVAS_W, 1));
      }
    };
    calcScale();
    window.addEventListener('resize', calcScale);
    return () => window.removeEventListener('resize', calcScale);
  }, [loading]);

  const handleDownload = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(certRef.current, { scale: 2, useCORS: true });
      const link = document.createElement('a');
      link.download = `certificate-${certificate.certificate_number}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Sertifikat berhasil diunduh!');
    } catch (e) {
      toast.error('Gagal mengunduh sertifikat');
    }
  };

  const handleShare = async () => {
    const verifyUrl = `${window.location.origin}/certificate/verify/${certificate.certificate_number}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Sertifikat: ${certificate.course_title}`,
          text: `Saya berhasil menyelesaikan kursus ${certificate.course_title}!`,
          url: verifyUrl
        });
      } else {
        await navigator.clipboard.writeText(verifyUrl);
        toast.success('Link verifikasi disalin!');
      }
    } catch (e) {
      toast.error('Gagal berbagi');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <Loader2 size={32} className="animate-spin text-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  if (!certificate) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="text-center">
        <Award size={48} className="mx-auto text-slate-300 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Sertifikat Tidak Ditemukan</h1>
        <Link to="/my/certificates" className="text-[var(--store-primary, #2D6CDF)] hover:underline mt-2 inline-block">Kembali</Link>
      </div>
    </div>
  );

  const elements = design.custom_elements || [];
  const hasCustomDesign = elements.length > 0;
  const bgColor = design.background_color || '#1e3a5f';
  const borderColor = design.border_color || design.accent_color || '#c9a227';
  const borderStyle = design.border_style || 'ornate';

  // Fallback colors for legacy (no custom_elements)
  const accentColor = design.accent_color || '#c9a227';
  const textColor = design.text_color || '#ffffff';

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="certificate-page">
      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="certificate-area"], [data-testid="certificate-area"] * { visibility: visible; }
          [data-testid="certificate-area"] { 
            position: absolute; left: 0; top: 0; width: 100vw; height: 100vh;
            margin: 0; padding: 0; border-radius: 0; box-shadow: none;
          }
        }
      `}</style>
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between print:hidden">
        <Link to="/my/certificates" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
          <ArrowLeft size={20} />
          <span>Kembali</span>
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleShare} data-testid="share-cert-btn">
            <Share2 size={16} className="mr-2" /> Bagikan
          </Button>
          <Button variant="outline" onClick={handlePrint} data-testid="print-cert-btn">
            <Printer size={16} className="mr-2" /> Cetak
          </Button>
          <Button onClick={handleDownload} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]" data-testid="download-cert-btn">
            <Download size={16} className="mr-2" /> Unduh
          </Button>
        </div>
      </div>

      {/* Certificate */}
      <div className="max-w-4xl mx-auto" ref={containerRef}>
        <div
          ref={certRef}
          data-testid="certificate-area"
          className="relative rounded-lg overflow-hidden shadow-2xl mx-auto"
          style={{ backgroundColor: bgColor, width: CANVAS_W * scale, height: CANVAS_H * scale }}
        >
          {/* Border Ornaments */}
          {borderStyle === 'ornate' && (
            <>
              <div className="absolute opacity-30" style={{ top: 12*scale, left: 12*scale, width: 48*scale, height: 48*scale, borderColor: borderColor, borderWidth: '2px', borderStyle: 'solid', borderRight: 'none', borderBottom: 'none' }} />
              <div className="absolute opacity-30" style={{ top: 12*scale, right: 12*scale, width: 48*scale, height: 48*scale, borderColor: borderColor, borderWidth: '2px', borderStyle: 'solid', borderLeft: 'none', borderBottom: 'none' }} />
              <div className="absolute opacity-30" style={{ bottom: 12*scale, left: 12*scale, width: 48*scale, height: 48*scale, borderColor: borderColor, borderWidth: '2px', borderStyle: 'solid', borderRight: 'none', borderTop: 'none' }} />
              <div className="absolute opacity-30" style={{ bottom: 12*scale, right: 12*scale, width: 48*scale, height: 48*scale, borderColor: borderColor, borderWidth: '2px', borderStyle: 'solid', borderLeft: 'none', borderTop: 'none' }} />
              <div className="absolute border opacity-20" style={{ top: 24*scale, left: 24*scale, right: 24*scale, bottom: 24*scale, borderColor: borderColor }} />
            </>
          )}
          {borderStyle === 'simple' && (
            <div className="absolute border-2" style={{ top: 16*scale, left: 16*scale, right: 16*scale, bottom: 16*scale, borderColor: borderColor }} />
          )}

          {/* Custom Elements from Drag & Drop Designer */}
          {hasCustomDesign ? (
            elements.map(el => <CertificateElement key={el.id} el={el} cert={certificate} scale={scale} />)
          ) : (
            /* Legacy fallback layout */
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ color: textColor, padding: 48 * scale }}>
              <Award size={64 * scale} style={{ color: accentColor }} className="mb-4" />
              <h1 className="uppercase font-light mb-2" style={{ color: accentColor, fontSize: 18 * scale, letterSpacing: 6 * scale }}>
                Certificate of Completion
              </h1>
              <div className="mx-auto mb-6" style={{ width: 128 * scale, height: 2 * scale, backgroundColor: accentColor }} />
              <p className="uppercase opacity-70 mb-2" style={{ fontSize: 12 * scale, letterSpacing: 3 * scale }}>This is to certify that</p>
              <h2 className="font-serif mb-6" style={{ fontFamily: 'Georgia, serif', fontSize: 36 * scale }}>
                {certificate.user_name}
              </h2>
              <p className="uppercase opacity-70 mb-2" style={{ fontSize: 12 * scale, letterSpacing: 3 * scale }}>has successfully completed</p>
              <h3 className="font-semibold mb-6" style={{ color: accentColor, fontSize: 22 * scale }}>
                {certificate.course_title}
              </h3>
              <p className="opacity-70 mb-8" style={{ fontSize: 13 * scale }}>
                {new Date(certificate.completed_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              {design.signature_enabled !== false && (
                <div className="mt-4">
                  <div className="mx-auto mb-2" style={{ width: 160 * scale, height: 1 * scale, backgroundColor: textColor, opacity: 0.3 }} />
                  <p className="font-semibold" style={{ fontSize: 14 * scale }}>{design.signature_name || certificate.instructor || 'Instructor'}</p>
                  <p className="opacity-70" style={{ fontSize: 11 * scale }}>{design.signature_title || 'Course Instructor'}</p>
                </div>
              )}
              <p className="absolute opacity-50" style={{ bottom: 24 * scale, fontSize: 10 * scale }}>
                Certificate ID: {certificate.certificate_number}
              </p>
            </div>
          )}
        </div>

        {/* Verification Badge */}
        <div className="mt-6 p-4 bg-white rounded-lg shadow-sm flex items-center justify-center gap-3 print:hidden">
          <CheckCircle size={20} className="text-emerald-500" />
          <span className="text-sm text-slate-600">
            Sertifikat dapat diverifikasi di: 
            <a href={`/certificate/verify/${certificate.certificate_number}`} className="text-[var(--store-primary, #2D6CDF)] ml-1 hover:underline">
              {window.location.origin}/certificate/verify/{certificate.certificate_number}
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
